import React, { FC, useEffect, useState } from 'react'
import { Redirect } from 'react-router'
import { Trans, t } from '@lingui/macro'
import { RouteComponentProps } from 'react-router-dom'

import { apolloClientPublic } from 'front-end/src/graphql/apolloClient'

import { staticLinks } from '../../../../shared/linkBuilder'
import { navigateTo } from '../../app/util/appHistory'
import { ButtonPrimary } from 'front-end/src/components'
import Form from '../../components/common/form/Form'
import Input from '../../components/common/form/input/Input'
import { notification } from '../../components/common/notifications/Notifications'
import { OauthLoginButtons } from './components/OauthLoginButtons'
import Loader from '../../components/common/loader/Loader'
import {
  useVerifyUserTokenMutation,
  useResetPasswordMutation,
  useUserByTokenQuery
} from './VerifyAccount.codegen'
import {
  PageWithSingleAlert,
  PageWithSingleAlertAndBackToLogin
} from '../PageWithSingleAlert'

import './Auth.less'
import IconArrowRight from 'front-end/src/components/common/icon/IconArrowRight'

function useVerifyTokenMutationFromUrl(token: string) {
  const [verifyToken, { data, loading: loading }] = useVerifyUserTokenMutation({
    client: apolloClientPublic
  })

  useEffect(() => {
    verifyToken({
      variables: {
        token
      }
    }).then(() => {
      notification.info(t`Your email address was verified successfully`)
    })
  }, [token])
  return { data, loading }
}

/**
 * just a simple component that verifies the user and redirects to profile
 */
export const VerifyAccountLoggedIn: FC<
  RouteComponentProps<{
    token: string
    userId: string
  }>
> = ({ match }) => {
  const { data, loading } = useVerifyTokenMutationFromUrl(match.params.token)

  if (loading || !data) {
    return <Loader />
  }

  const { verifyUser } = data

  if (!verifyUser) {
    return (
      <PageWithSingleAlert
        description={t`Please check your URL and try again`}
        title={t`Invalid token`}
      />
    )
  }

  // user navigated here from his/her email. We should verify it and show some message
  return <Redirect to={staticLinks.userBasicTab} />
}

/**
 * a more complex component that verifies the user and resets the users password, then navigates to login
 */
export const VerifyAccountPublic: FC<
  RouteComponentProps<{
    token: string
    userId: string
  }>
> = ({ match }) => {
  const [password, setPassword] = useState('')
  const [resetPasswordMutation, { loading }] = useResetPasswordMutation()
  const { loading: loadingMutation } = useVerifyTokenMutationFromUrl(
    match.params.token
  )
  const { data, loading: loadingQuery } = useUserByTokenQuery({
    fetchPolicy: 'cache-first',
    variables: {
      token: match.params.token
    }
  })

  if (loadingQuery || !data || loadingMutation) {
    return <Loader />
  }

  const { user } = data

  if (!user) {
    return (
      <PageWithSingleAlertAndBackToLogin
        description={t`Please check your URL and try again`}
        title={t`Invalid token`}
      />
    )
  }
  if (user.hasOwnPasswordSet) {
    return <Redirect to={staticLinks.login} /> // user doesn't need a new password-they know the old one
  }

  return (
    <div className="Auth">
      <div className="Auth__container">
        <h3>
          <Trans>Create a password</Trans>
        </h3>

        <Form>
          <div className="form-row">
            <label htmlFor="inputPassword">
              <Trans>
                Please set a password to use with your account{' '}
                <strong>{user.userIdentity}</strong>. Passwords must be at least
                8 characters containing both letters and numbers.
              </Trans>
            </label>

            <Input
              placeholder="Password"
              id="inputPassword"
              autoComplete="off"
              fullWidth
              value={password}
              onChange={(value) => {
                setPassword(value)
              }}
              type="password"
            />
          </div>

          <div className="form-row">
            <ButtonPrimary
              isFullWidth
              isLoading={loading}
              disabled={loading}
              onClick={async () => {
                const resetPasswordResult = await resetPasswordMutation({
                  variables: {
                    userId: Number(match.params.userId),
                    password,
                    token: match.params.token
                  }
                })
                const userIdentity =
                  resetPasswordResult.data?.resetPassword?.userIdentity

                if (userIdentity) {
                  notification.info(
                    t`Password successfully updated for ${userIdentity}`
                  )
                }

                navigateTo(staticLinks.login)
              }}
              rightIcon={<IconArrowRight />}
            >
              <span className="btn__text">
                <Trans>Set password</Trans>
              </span>
            </ButtonPrimary>
          </div>

          <OauthLoginButtons />
        </Form>
      </div>
    </div>
  )
}
